'use client';

import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { setStoredStoreId, getStoredStoreId } from '@/lib/store-storage';
import { useAuth } from '@/contexts/AuthContext';
import {
  CART_STORAGE_KEY,
  CART_STORE_PREFIX,
  packCartFromLocalStorage,
  applyPackedCartToLocalStorage,
} from '@/lib/cart-persistence';
import {
  mergeSavedCartPayloads,
  normalizeRemoteCartPayload,
} from '@/lib/cart-merge';
import type { SavedCartPayloadV1, PromoCodeState } from '@/lib/cart-persistence';

export type CartItem = {
  productId: string;
  name: string;
  catalogItemId: string;
  packageId?: string | null;
  price: number;
  quantity: number;
  actualPackageId?: string | null;
  inStock?: number;
};

export type FulfillmentMode = 'pickup' | 'delivery';

type CartState = {
  storeCovaLocationId: number | null;
  items: CartItem[];
  fulfillmentMode: FulfillmentMode;
  promoCode: PromoCodeState | null;
};

const defaultState: CartState = {
  storeCovaLocationId: null,
  items: [],
  fulfillmentMode: 'pickup',
  promoCode: null,
};

type CartContextValue = {
  state: CartState;
  setStore: (covaLocationId: number) => void;
  setFulfillmentMode: (mode: FulfillmentMode) => void;
  addItem: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void;
  removeItem: (productId: string, packageId?: string | null) => void;
  updateQty: (productId: string, packageId: string | null | undefined, quantity: number) => void;
  clear: () => void;
  setPromoCode: (promoCode: PromoCodeState | null) => void;
  total: number;
  // Cart drawer state
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  isInitialized: boolean;
};

const CartContext = createContext<CartContextValue | undefined>(undefined);

const API_BASE =
  typeof process !== 'undefined'
    ? process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000'
    : 'http://localhost:4000';

export function CartProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState<CartState>(defaultState);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load cart from storage on mount (only once)
  useEffect(() => {
    if (isInitialized) return;
    
    try {
      // First, try to get store ID from dedicated storage
      const storedStoreId = getStoredStoreId();
      
      // Load current store selection
      const raw = localStorage.getItem(CART_STORAGE_KEY);
      if (raw) {
        const savedState = JSON.parse(raw);
        const currentStoreId = storedStoreId || savedState.storeCovaLocationId;
        
        // Load items for the current store
        if (currentStoreId) {
          const storeKey = `${CART_STORE_PREFIX}${currentStoreId}`;
          const storeCartRaw = localStorage.getItem(storeKey);
          if (storeCartRaw) {
            const storeCart = JSON.parse(storeCartRaw);
            setState({
              ...savedState,
              items: storeCart.items || [],
              promoCode: storeCart.promoCode || null
            });
            setIsInitialized(true);
            return;
          }
        }
        
        // Check for temp items (items added before store was selected)
        const tempKey = `${CART_STORE_PREFIX}_temp`;
        const tempCartRaw = localStorage.getItem(tempKey);
        if (tempCartRaw) {
          const tempCart = JSON.parse(tempCartRaw);
          setState({
            ...savedState,
            items: tempCart.items || [],
            promoCode: tempCart.promoCode || null
          });
          setIsInitialized(true);
          return;
        }
        
        // Fallback to old format or default
        setState({
          ...savedState,
          items: savedState.items || [],
          promoCode: savedState.promoCode || null
        });
      }
    } catch (err) {
      console.error('[CartContext] Failed to load cart from storage:', err);
    }
    setIsInitialized(true);
  }, [isInitialized]);

  // Save store selection and fulfillment mode to storage
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({
        storeCovaLocationId: state.storeCovaLocationId,
        fulfillmentMode: state.fulfillmentMode
      }));
      // Also save to dedicated store ID storage
      if (state.storeCovaLocationId) {
        setStoredStoreId(state.storeCovaLocationId);
      }
    } catch {}
  }, [state.storeCovaLocationId, state.fulfillmentMode]);

  // Save items and promo code per store
  // Only save after initialization to avoid overwriting during initial load
  useEffect(() => {
    if (!isInitialized) return;
    
    if (state.storeCovaLocationId) {
      try {
        const storeKey = `${CART_STORE_PREFIX}${state.storeCovaLocationId}`;
        localStorage.setItem(storeKey, JSON.stringify({
          items: state.items,
          promoCode: state.promoCode
        }));
      } catch (err) {
        console.error('[CartContext] Failed to save cart to storage:', err);
      }
    } else if (state.items.length > 0) {
      // If items exist but no store is set, try to save to a temporary location
      // This handles the case where items are added before store selection
      try {
        const tempKey = `${CART_STORE_PREFIX}_temp`;
        localStorage.setItem(tempKey, JSON.stringify({
          items: state.items,
          promoCode: state.promoCode
        }));
      } catch (err) {
        console.error('[CartContext] Failed to save temp cart to storage:', err);
      }
    }
  }, [state.items, state.promoCode, state.storeCovaLocationId, isInitialized]);

  const applyPackedPayloadToState = useCallback((p: SavedCartPayloadV1) => {
    applyPackedCartToLocalStorage(p);
    const sid = p.storeCovaLocationId;
    let items: CartItem[] = [];
    let promo: PromoCodeState | null = null;
    if (sid != null && p.perStore[String(sid)]) {
      items = p.perStore[String(sid)].items as CartItem[];
      promo = p.perStore[String(sid)].promoCode;
    } else if (p.temp.items.length > 0) {
      items = p.temp.items as CartItem[];
      promo = p.temp.promoCode;
    }
    setState({
      storeCovaLocationId: sid,
      fulfillmentMode: p.fulfillmentMode,
      items,
      promoCode: promo,
    });
    if (sid) setStoredStoreId(sid);
  }, []);

  /**
   * After login/signup we additive-merge (guest + account). On refresh we keep local cart
   * and only restore from the server when this device has no cart lines (other device / cleared data).
   */
  useEffect(() => {
    if (!isInitialized || authLoading || !user?.id) return;

    let active = true;
    (async () => {
      try {
        const token =
          typeof window !== 'undefined'
            ? localStorage.getItem('accessToken')
            : null;
        if (!token) return;

        const pendingLoginMerge =
          typeof window !== 'undefined' &&
          sessionStorage.getItem('dank:pending-login-cart-merge') === '1';

        const localPacked = packCartFromLocalStorage();
        const res = await fetch(`${API_BASE}/api/auth/cart`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (!res.ok || !active) return;
        const data = (await res.json().catch(() => ({}))) as {
          success?: boolean;
          cart?: unknown;
        };
        if (!data?.success || !active) return;

        const remoteNorm = normalizeRemoteCartPayload(data.cart);
        const hadRemote =
          data.cart != null &&
          (Object.keys(remoteNorm.perStore).length > 0 ||
            (remoteNorm.temp?.items?.length ?? 0) > 0);
        const hadDevice =
          Object.keys(localPacked.perStore).some(
            (k) => (localPacked.perStore[k]?.items?.length ?? 0) > 0
          ) || (localPacked.temp.items?.length ?? 0) > 0;

        if (pendingLoginMerge) {
          try {
            sessionStorage.removeItem('dank:pending-login-cart-merge');
          } catch {
            /* ignore */
          }
          const { merged } = mergeSavedCartPayloads(
            localPacked,
            remoteNorm
          );
          if (!active) return;
          applyPackedPayloadToState(merged);
          return;
        }

        if (!hadRemote) return;
        if (hadDevice) {
          return;
        }

        if (!active) return;
        applyPackedPayloadToState(remoteNorm);
      } catch {
        /* ignore */
      }
    })();

    return () => {
      active = false;
    };
  }, [isInitialized, authLoading, user?.id, applyPackedPayloadToState]);

  // Debounced server persistence for signed-in users (issue #44).
  useEffect(() => {
    if (!isInitialized || authLoading || !user?.id) return;
    const token =
      typeof window !== 'undefined'
        ? localStorage.getItem('accessToken')
        : null;
    if (!token) return;

    const t = window.setTimeout(() => {
      void (async () => {
        try {
          const packed = packCartFromLocalStorage();
          await fetch(`${API_BASE}/api/auth/cart`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            credentials: 'include',
            body: JSON.stringify(packed),
          });
        } catch {
          /* ignore */
        }
      })();
    }, 1100);

    return () => window.clearTimeout(t);
  }, [
    user?.id,
    authLoading,
    isInitialized,
    state.items,
    state.storeCovaLocationId,
    state.fulfillmentMode,
    state.promoCode,
  ]);

  const setStore = useCallback((covaLocationId: number) => {
    // Save to dedicated store ID storage
    setStoredStoreId(covaLocationId);
    
    setState((s) => {
      // Save current store's cart before switching
      if (s.storeCovaLocationId) {
        try {
          const currentStoreKey = `${CART_STORE_PREFIX}${s.storeCovaLocationId}`;
          localStorage.setItem(currentStoreKey, JSON.stringify({
            items: s.items,
            promoCode: s.promoCode
          }));
        } catch {}
      }
      
      // Load new store's cart
      const newStoreKey = `${CART_STORE_PREFIX}${covaLocationId}`;
      let newItems: CartItem[] = [];
      let newPromoCode: PromoCodeState | null = null;
      
      try {
        const newStoreCartRaw = localStorage.getItem(newStoreKey);
        if (newStoreCartRaw) {
          const newStoreCart = JSON.parse(newStoreCartRaw);
          newItems = newStoreCart.items || [];
          newPromoCode = newStoreCart.promoCode || null;
        } else {
          // If no cart exists for this store, check for temp items
          // (items added before store was selected)
          const tempKey = `${CART_STORE_PREFIX}_temp`;
          const tempCartRaw = localStorage.getItem(tempKey);
          if (tempCartRaw) {
            const tempCart = JSON.parse(tempCartRaw);
            newItems = tempCart.items || [];
            newPromoCode = tempCart.promoCode || null;
            // Clear temp storage after migrating
            localStorage.removeItem(tempKey);
          }
        }
      } catch {}
      
      return {
        ...s,
        storeCovaLocationId: covaLocationId,
        items: newItems,
        promoCode: newPromoCode
      };
    });
  }, []);

  const setFulfillmentMode = useCallback((mode: FulfillmentMode) => {
    setState((s) => ({ ...s, fulfillmentMode: mode }));
  }, []);

  const addItem: CartContextValue['addItem'] = useCallback((item, quantity = 1) => {
    setState((s) => {
      const match = (ci: CartItem) => ci.productId === item.productId && (ci.packageId ?? null) === (item.packageId ?? null);
      const existing = s.items.find(match);
      const stockLimit = (item as CartItem).inStock ?? Infinity;
      if (existing) {
        const existingStock = existing.inStock ?? stockLimit;
        const newQty = Math.min(existing.quantity + quantity, existingStock);
        return { ...s, items: s.items.map((ci) => (match(ci) ? { ...ci, quantity: newQty } : ci)) };
      }
      const clampedQty = Math.min(quantity, stockLimit);
      return { ...s, items: [...s.items, { ...item, quantity: clampedQty }] };
    });
  }, []);

  const removeItem = useCallback((productId: string, packageId?: string | null) => {
    setState((s) => ({ ...s, items: s.items.filter((ci) => !(ci.productId === productId && (ci.packageId ?? null) === (packageId ?? null))) }));
  }, []);

  const updateQty = useCallback((productId: string, packageId: string | null | undefined, quantity: number) => {
    setState((s) => ({
      ...s,
      items: s.items.map((ci) => {
        if (ci.productId !== productId || (ci.packageId ?? null) !== (packageId ?? null)) return ci;
        const max = ci.inStock ?? Infinity;
        return { ...ci, quantity: Math.min(Math.max(1, quantity), max) };
      }),
    }));
  }, []);

  const clear = useCallback(() => {
    setState((s) => {
      // Clear items and promo code from state
      const clearedState = { ...s, items: [], promoCode: null };
      
      // Also clear from localStorage for the current store
      if (s.storeCovaLocationId) {
        try {
          const storeKey = `${CART_STORE_PREFIX}${s.storeCovaLocationId}`;
          localStorage.setItem(storeKey, JSON.stringify({
            items: [],
            promoCode: null
          }));
        } catch (err) {
          console.error('[CartContext] Failed to clear cart from storage:', err);
        }
      }
      
      // Also clear temp storage if it exists
      try {
        const tempKey = `${CART_STORE_PREFIX}_temp`;
        localStorage.removeItem(tempKey);
      } catch (err) {
        // Ignore errors when clearing temp storage
      }
      
      return clearedState;
    });
  }, []);

  const setPromoCode = useCallback((promoCode: PromoCodeState | null) => {
    setState((s) => ({ ...s, promoCode }));
  }, []);

  const total = useMemo(() => state.items.reduce((sum, it) => sum + it.price * it.quantity, 0), [state.items]);

  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);

  const value: CartContextValue = { 
    state, 
    setStore, 
    setFulfillmentMode, 
    addItem, 
    removeItem, 
    updateQty, 
    clear,
    setPromoCode,
    total,
    isCartOpen,
    openCart,
    closeCart,
    isInitialized,
  };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}

/**
 * Safe variant for contexts that may render in environments without CartProvider
 * (e.g. admin previews). Returns `null` when cart context is unavailable.
 */
export function useCartOptional() {
  return useContext(CartContext);
}


